import React from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import NotFound from "@/pages/NotFound";
import { useLang } from "@/i18n";
import { useCart } from "@/context/CartContext";
import { legalDocs } from "@/lib/legalDrafts";
import { LEGAL_ENABLED } from "@/lib/config";

const Legal = () => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const { shipping } = useCart();
  const doc = legalDocs(shipping)[slug];

  if (!LEGAL_ENABLED || !doc) return <NotFound />;

  return (
    <div className="bg-[#F5F1E8] min-h-screen overflow-x-hidden">
      <Seo
        title={`${doc.title} · Naguarà`}
        description={t.seo.legal.description}
        path={`/legal/${slug}`}
        lang={lang}
        noindex
      />
      <Navbar />
      <div className="h-20 bg-[#422D28]" aria-hidden="true" />

      <main data-testid={`legal-${slug}`} className="px-6 md:px-12 lg:px-20 py-16 md:py-20">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 min-h-[44px] text-[#3B2523]/70 text-sm font-semibold hover:text-[#3B2523] transition-colors"
          >
            <ArrowLeft size={16} />
            {t.producto.back}
          </Link>

          <div
            data-testid="legal-draft-banner"
            role="note"
            className="mt-6 flex gap-3 rounded-2xl border border-[#8C2F1E]/30 bg-[#8C2F1E]/[0.07] px-5 py-4"
          >
            <AlertTriangle size={20} className="text-[#8C2F1E] shrink-0 mt-0.5" />
            <p className="text-[#8C2F1E] text-sm leading-relaxed">{t.legal.draft}</p>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[#3B2523] mt-10">
            {doc.title}
          </h1>
          <p className="text-[#3B2523]/70 text-base md:text-lg mt-5 leading-relaxed">{doc.intro}</p>

          <div className="mt-12 space-y-10">
            {doc.sections.map((s) => (
              <section key={s.h}>
                <h2 className="font-display text-xl md:text-2xl font-bold text-[#3B2523]">{s.h}</h2>
                <div className="mt-3 space-y-2">
                  {s.p.map((line, i) => (
                    <p key={i} className="text-[#3B2523]/70 text-base leading-relaxed">
                      {line.split("{{pendiente}}").map((part, j, arr) => (
                        <React.Fragment key={j}>
                          {part}
                          {j < arr.length - 1 && (
                            <span
                              data-testid="legal-pending-tag"
                              className="inline-block align-baseline bg-[#3B2523]/8 border border-[#3B2523]/15 text-[#3B2523]/70 text-xs font-semibold uppercase tracking-[0.08em] rounded-full px-2.5 py-0.5"
                            >
                              {t.legal.pending}
                            </span>
                          )}
                        </React.Fragment>
                      ))}
                    </p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Legal;
